import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiCheck,
  FiChevronLeft,
  FiMinus,
  FiPlus,
  FiShoppingCart,
  FiTrash2,
  FiUserPlus,
} from "react-icons/fi";
import {
  useCreateSaleMutation,
  useGetCustomersQuery,
  useGetPOSAvailableStockQuery,
} from "../services/api";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", {
    style: "currency",
    currency: "ETB",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function getCartLineTotal(item) {
  return Number(item.unit_price || 0) * Number(item.custom_quantity || 0);
}

export default function POSCheckoutPage() {
  const navigate = useNavigate();
  const { data: customersData } = useGetCustomersQuery();
  const { refetch: refetchStock } = useGetPOSAvailableStockQuery();
  const [createSale, { isLoading: isSaving }] = useCreateSaleMutation();

  const customers = customersData?.data || [];

  // Get cart from localStorage
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("pos_cart");
    return saved ? JSON.parse(saved) : [];
  });

  const [customerId, setCustomerId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [customPrice, setCustomPrice] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const subtotal = cart.reduce((sum, item) => sum + getCartLineTotal(item), 0);
  const total = customPrice && Number(customPrice) > 0 ? Number(customPrice) : subtotal;
  const discount = subtotal - total;

  const updateCartItem = (index, changes) => {
    const newCart = cart.map((item, itemIndex) =>
      itemIndex === index ? { ...item, ...changes } : item
    );
    setCart(newCart);
    localStorage.setItem("pos_cart", JSON.stringify(newCart));
  };

  const changeQuantity = (index, direction) => {
    const item = cart[index];
    const nextQuantity = Math.max(1, Number(item.quantity || 1) + direction);
    updateCartItem(index, {
      quantity: nextQuantity,
      pieces_count: nextQuantity,
      custom_quantity: Number((Number(item.weight_grams || 1) * nextQuantity).toFixed(2)),
    });
  };

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, itemIndex) => itemIndex !== index);
    setCart(newCart);
    localStorage.setItem("pos_cart", JSON.stringify(newCart));
  };

  const handleCheckout = async () => {
    setError("");
    setSuccess("");

    if (cart.length === 0) {
      setError("Add at least one item before checkout.");
      return;
    }

    const invalidItems = cart.filter((item) => !item.custom_quantity || Number(item.custom_quantity) <= 0);
    if (invalidItems.length > 0) {
      setError("Every cart item needs a valid quantity.");
      return;
    }

    const overStockItem = cart.find((item) => Number(item.custom_quantity) > Number(item.available_quantity));
    if (overStockItem) {
      setError(`${overStockItem.product_name} exceeds available stock.`);
      return;
    }

    try {
      const salePayload = {
        customer_id: customerId || null,
        items: cart.map((item) => ({
          product_id: item.product_id,
          quantity: Number(item.quantity || 1),
          unit_price: Number(item.unit_price || 0),
          stock_item_id: item.stock_item_id,
          custom_quantity: Number(item.custom_quantity),
          custom_unit: item.custom_unit,
          storage_note: item.storage_note || null,
        })),
        payment_method: paymentMethod,
        discount: Number(discount || 0),
      };

      const result = await createSale(salePayload).unwrap();
      setSuccess(`Sale completed! Receipt ${result.data.sale_number}`);
      
      // Clear cart
      setCart([]);
      localStorage.removeItem("pos_cart");
      setCustomerId("");
      setCustomPrice("");
      setPaymentMethod("cash");
      refetchStock();
      
      // Redirect back to POS after 2 seconds
      setTimeout(() => {
        navigate("/pos");
      }, 2000);
    } catch (err) {
      setError(err?.data?.message || "Failed to complete sale.");
    }
  };

  // Redirect if cart is empty
  if (cart.length === 0 && !success) {
    setTimeout(() => navigate("/pos"), 100);
    return null;
  }

  return (
    <div style={{
      position: "fixed",
      top: "64px",
      left: "240px",
      right: 0,
      bottom: 0,
      background: "#F7F8FA",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      {/* Page Header */}
      <div style={{
        background: "white",
        borderBottom: "1px solid #E2E8F0",
        padding: "12px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={() => navigate("/pos")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              border: "1px solid #E2E8F0",
              background: "white",
              borderRadius: "8px",
              cursor: "pointer",
              color: "#64748B",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            <FiChevronLeft size={16} />
            Back to Products
          </button>
          <div>
            <h1 style={{ fontSize: "1.5rem", fontWeight: 700, margin: 0, marginBottom: "4px", color: "#14213D" }}>
              Checkout
            </h1>
            <p style={{ fontSize: "0.8125rem", color: "#64748B", margin: 0 }}>
              Review and complete your sale
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div style={{
          margin: "12px 24px 0",
          padding: "10px 16px",
          background: "#D1FAE5",
          border: "1px solid #10B981",
          borderRadius: "8px",
          color: "#047857",
          fontSize: "0.875rem",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexShrink: 0,
        }}>
          <FiCheck size={16} />
          {success}
        </div>
      )}

      {/* Checkout Content */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "24px",
      }}>
        <div style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 480px",
          gap: "24px",
        }}>
          {/* Left: Cart Items Review */}
          <div style={{
            background: "white",
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
            padding: "24px",
          }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "20px", color: "#14213D" }}>
              Order Items ({cart.length})
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {cart.map((item, index) => (
                <div
                  key={`${item.product_id}-${item.stock_item_id}`}
                  style={{
                    padding: "16px",
                    background: "#F9FAFB",
                    borderRadius: "8px",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "4px", color: "#14213D" }}>
                        {item.product_name}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#94A3B8", marginBottom: "2px" }}>
                        SKU: {item.sku}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#64748B" }}>
                        {item.material_type}
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(index)}
                      style={{
                        width: "32px",
                        height: "32px",
                        border: "none",
                        background: "#FEE2E2",
                        cursor: "pointer",
                        color: "#DC2626",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                      }}
                      title="Remove item"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>

                  <div style={{ display: "flex", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: "6px", fontWeight: 600 }}>
                        Quantity
                      </div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          onClick={() => changeQuantity(index, -1)}
                          style={{
                            width: "36px",
                            height: "36px",
                            border: "1px solid #E2E8F0",
                            background: "white",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#14213D",
                          }}
                        >
                          <FiMinus size={14} />
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const quantity = Math.max(1, Number(e.target.value || 1));
                            updateCartItem(index, {
                              quantity,
                              pieces_count: quantity,
                              custom_quantity: Number((Number(item.weight_grams || 1) * quantity).toFixed(2)),
                            });
                          }}
                          style={{
                            flex: 1,
                            height: "36px",
                            border: "1px solid #E2E8F0",
                            borderRadius: "8px",
                            textAlign: "center",
                            fontSize: "0.9375rem",
                            fontWeight: 600,
                            color: "#14213D",
                          }}
                        />
                        <button
                          onClick={() => changeQuantity(index, 1)}
                          style={{
                            width: "36px",
                            height: "36px",
                            border: "1px solid #E2E8F0",
                            background: "white",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#14213D",
                          }}
                        >
                          <FiPlus size={14} />
                        </button>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: "6px", fontWeight: 600 }}>
                        {item.product_unit === 'grams' ? 'Grams' : 'Weight (g)'}
                      </div>
                      <input
                        type="number"
                        value={item.custom_quantity}
                        onChange={(e) => updateCartItem(index, { custom_quantity: e.target.value })}
                        style={{
                          width: "100%",
                          height: "36px",
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px",
                          padding: "0 12px",
                          fontSize: "0.9375rem",
                          color: "#14213D",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: "12px",
                    marginTop: "12px",
                    borderTop: "1px solid #E2E8F0",
                  }}>
                    <span style={{ fontSize: "0.875rem", color: "#64748B" }}>
                      {formatCurrency(item.unit_price)} × {item.custom_quantity}
                    </span>
                    <span style={{ fontSize: "1.125rem", fontWeight: 700, color: "#F5B82E" }}>
                      {formatCurrency(getCartLineTotal(item))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Checkout Form */}
          <div style={{
            background: "white",
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
            padding: "24px",
            height: "fit-content",
            position: "sticky",
            top: "24px",
          }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "20px", color: "#14213D" }}>
              Checkout Details
            </h2>

            {/* Customer */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontSize: "0.875rem", fontWeight: 600, display: "block", marginBottom: "8px", color: "#14213D" }}>
                Customer
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  style={{
                    flex: 1,
                    height: "42px",
                    padding: "0 12px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    fontSize: "0.9375rem",
                    color: "#14213D",
                  }}
                >
                  <option value="">Walk-in Customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
                <button
                  style={{
                    width: "42px",
                    height: "42px",
                    border: "1px solid #E2E8F0",
                    background: "white",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#64748B",
                  }}
                  title="Add Customer"
                >
                  <FiUserPlus size={18} />
                </button>
              </div>
            </div>

            {/* Payment Method */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: 600, marginBottom: "8px", color: "#14213D" }}>
                Payment Method
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                {["cash", "bank_transfer", "mobile_money"].map((method) => (
                  <button
                    key={method}
                    onClick={() => setPaymentMethod(method)}
                    style={{
                      height: "48px",
                      border: `2px solid ${paymentMethod === method ? "#F5B82E" : "#E2E8F0"}`,
                      background: paymentMethod === method ? "#FFF8E7" : "white",
                      borderRadius: "8px",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      color: paymentMethod === method ? "#F5B82E" : "#64748B",
                    }}
                  >
                    {method === "cash" ? "Cash" : method === "bank_transfer" ? "Bank" : "Mobile"}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Price */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontSize: "0.875rem", fontWeight: 600, display: "block", marginBottom: "8px", color: "#14213D" }}>
                Custom Price (Optional)
              </label>
              <input
                type="number"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                placeholder={formatCurrency(subtotal)}
                style={{
                  width: "100%",
                  height: "42px",
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  padding: "0 12px",
                  fontSize: "0.9375rem",
                  color: "#14213D",
                }}
              />
            </div>

            {/* Order Summary */}
            <div style={{
              background: "#F9FAFB",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9375rem", marginBottom: "8px" }}>
                <span style={{ color: "#64748B" }}>Subtotal</span>
                <span style={{ fontWeight: 600, color: "#14213D" }}>{formatCurrency(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9375rem", marginBottom: "8px" }}>
                  <span style={{ color: "#10B981" }}>Discount</span>
                  <span style={{ fontWeight: 600, color: "#10B981" }}>-{formatCurrency(discount)}</span>
                </div>
              )}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "1.5rem",
                fontWeight: 700,
                paddingTop: "12px",
                borderTop: "2px solid #E2E8F0",
                marginTop: "12px",
              }}>
                <span style={{ color: "#14213D" }}>TOTAL</span>
                <span style={{ color: "#F5B82E" }}>{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                padding: "12px",
                background: "#FEE2E2",
                border: "1px solid #EF4444",
                borderRadius: "8px",
                color: "#DC2626",
                fontSize: "0.875rem",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <FiAlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Complete Sale Button */}
            <button
              onClick={handleCheckout}
              disabled={isSaving}
              style={{
                width: "100%",
                height: "56px",
                background: isSaving ? "#9CA3AF" : "linear-gradient(135deg, #F5B82E 0%, #E5A820 100%)",
                color: "#0F1F3D",
                border: "none",
                borderRadius: "12px",
                fontSize: "1.125rem",
                fontWeight: 800,
                cursor: isSaving ? "not-allowed" : "pointer",
                boxShadow: isSaving ? "none" : "0 4px 16px rgba(245, 184, 46, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1)",
                transition: "all 0.2s",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
              onMouseEnter={(e) => {
                if (!isSaving) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(245, 184, 46, 0.5), 0 4px 12px rgba(0, 0, 0, 0.15)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = isSaving ? "none" : "0 4px 16px rgba(245, 184, 46, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1)";
              }}
            >
              {isSaving ? "⏳ Processing..." : "✓ COMPLETE SALE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
